
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';
import { notifyEmployeeFeedbackReady } from '../lib/notifications';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C', goldDim: '#8a6e2a',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
  green: '#1D9E75', greenDeep: '#0a1f15', greenDim: '#0f6e56',
};

const PRIORITY_META = [
  { key: 'must', label: 'Must do', tag: '#c0392b', bg: '#0f0810', border: '#4a1530' },
  { key: 'should', label: 'Should do', tag: '#C9A84C', bg: '#0f0e08', border: '#4a3a10' },
  { key: 'could', label: 'Could do', tag: '#1D9E75', bg: '#080f0e', border: '#0e3a2a' },
];

const GOAL_STATUSES = {
  done: { label: 'Done', color: '#1D9E75', bg: '#0a1f15', border: '#0f6e56' },
  in_progress: { label: 'In Progress', color: '#C9A84C', bg: '#0f0d05', border: '#8a6e2a' },
  not_started: { label: 'Not Started', color: '#444466', bg: '#11111a', border: '#1e1a2e' },
  deprioritised: { label: 'Deprioritised', color: '#8888aa', bg: '#0a0a0f', border: '#2e2850' },
};

const initials = n => n?.split(' ').map(x => x[0]).join('') || '?';

export default function MidYearManagerReview() {
  const { employee } = useUser();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selected, setSelected] = useState(null);
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [employee]);

  const fetchData = async () => {
    if (!employee) return;
    setLoading(true);
    // try/finally so any throw still clears the spinner.
    try {
      const { data: midCycle } = await supabase.from('review_cycles').select('*').eq('is_open', true).eq('type', 'Mid-Year').single();
      if (!midCycle) return;
      setCycle(midCycle);

      // Direct reports = active people whose effective reviewer is me
      // (review_manager_email override wins, else Okta manager_email), matched
      // case-insensitively and WITHOUT a user_type filter — this mirrors the
      // midyear_reviews RLS policy and the submit/notify path exactly. A
      // stricter filter here silently hid a submitted review from the manager
      // (e.g. a report whose user_type wasn't exactly 'Employee', or a manager
      // email differing only by case). RLS on `employees` already limits this
      // SELECT to my own row + my reports, so we just drop self.
      const me = (employee.email || '').toLowerCase();
      const { data: visible } = await supabase.from('employees')
        .select('id,name,email,department,review_manager_email,manager_email').eq('active', true);
      const reports = (visible || []).filter(e =>
        e.id !== employee.id &&
        // nullish-coalesce to mirror SQL coalesce(review_manager_email, manager_email):
        // an empty-string override stays empty (unlike ||), matching effective_manager_email + RLS.
        (e.review_manager_email ?? e.manager_email ?? '').toLowerCase() === me
      );
      if (!reports.length) return;

      const reportIds = reports.map(r => r.id);
      const { data: reviewData } = await supabase.from('midyear_reviews').select('*').in('employee_id', reportIds).eq('cycle_id', midCycle.id);

      const merged = reports.map(r => ({ employee: r, review: reviewData?.find(rv => rv.employee_id === r.id) || null }));
      setReviews(merged);
    } finally {
      setLoading(false);
    }
  };

  const openReview = item => {
    setSelected(item);
    setComments(item.review?.manager_comments || '');
    setSubmitted(item.review?.manager_status === 'completed');
    setSavedAt(null); setSaveMsg('');
  };

  const flash = msg => { setSaveMsg(msg); setTimeout(() => setSaveMsg(''), 2500); };

  const saveDraft = async () => {
    if (!selected?.review || !comments.trim()) return;
    setSaving(true);
    await supabase.from('midyear_reviews').update({ manager_comments: comments, updated_at: new Date().toISOString() }).eq('id', selected.review.id);
    const now = new Date();
    setSavedAt(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`);
    flash('Draft saved');
    setSaving(false);
  };

  const submitReview = async () => {
    if (!selected?.review || !comments.trim()) return;
    setSaving(true);
    await supabase.from('midyear_reviews').update({ manager_comments: comments, manager_status: 'completed', manager_reviewed_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq('id', selected.review.id);
    await notifyEmployeeFeedbackReady({ employeeEmail: selected.employee.email, managerName: employee.name, cycleLabel: cycle.label });
    setSubmitted(true);
    setSaving(false);
    fetchData();
  };

  const pending = reviews.filter(r => r.review?.emp_status === 'submitted' && r.review?.manager_status !== 'completed').length;
  const completed = reviews.filter(r => r.review?.manager_status === 'completed').length;

  const Header = ({ sub }) => (
    <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {sub && <button onClick={() => { setSelected(null); setSubmitted(false); }} style={{ background: 'none', border: 'none', color: B.textSecondary, cursor: 'pointer', fontSize: 12, letterSpacing: '0.05em', padding: 0, marginRight: 8 }}>← BACK</button>}
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />
        </button>
        <span style={{ width: 1, height: 16, background: B.border, margin: '0 8px' }} />
        <span style={{ fontSize: 11, color: B.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Manager Portal</span>
        {!sub && <button onClick={() => navigate('/')} style={{ marginLeft: 16, fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>← BACK TO PORTAL</button>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {savedAt && <span style={{ fontSize: 11, color: B.textMuted }}>Draft saved at {savedAt}</span>}
        {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 11, color: B.textSecondary, margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{cycle?.label}</p>
          <p style={{ fontSize: 12, color: B.textPrimary, margin: '2px 0 0' }}>{employee?.name}</p>
        </div>
      </div>
    </div>
  );

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, color: B.textSecondary, fontSize: 12, letterSpacing: '0.08em' }}>LOADING...</div>;

  if (submitted && selected) return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header sub />
      <div style={{ padding: '3rem 2rem', textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purple}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: 20, color: B.purple }}>✓</div>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: B.textPrimary, marginBottom: 10 }}>Mid-year review submitted</h2>
        <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7, marginBottom: 24 }}>{selected.employee.name} has been notified that your mid-year feedback is ready.</p>
        <button onClick={() => { setSelected(null); setSubmitted(false); }} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 24px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>BACK TO INBOX</button>
      </div>
    </div>
  );

  if (selected) {
    const { employee: emp, review } = selected;
    const isCompleted = review?.manager_status === 'completed';
    const notSubmitted = !review || review.emp_status !== 'submitted';
    const canSubmit = comments.trim().length > 0;

    return (
      <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
        <Header sub />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: B.purple }}>{initials(emp.name)}</div>
              <div>
                <p style={{ fontSize: 14, color: B.textPrimary, margin: 0 }}>{emp.name}</p>
                <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0' }}>{emp.department}</p>
              </div>
            </div>
            {isCompleted && <span style={{ fontSize: 10, letterSpacing: '0.06em', padding: '3px 10px', borderRadius: 99, background: B.purpleDeep, color: B.purple, border: `0.5px solid ${B.purpleDim}` }}>COMPLETED</span>}
          </div>

          {notSubmitted ? (
            <div style={{ background: '#0f0d05', border: `0.5px solid ${B.goldDim}`, borderRadius: 8, padding: '1rem 1.25rem' }}>
              <p style={{ fontSize: 13, color: B.textSecondary, margin: 0 }}>This employee has not yet submitted their mid-year form. You will be notified when it is ready.</p>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 14, textTransform: 'uppercase' }}>Employee priority progress</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                {PRIORITY_META.map(({ key, label, tag, bg, border }) => {
                  const statusKey = review?.[`${key}_status`];
                  const st = statusKey ? GOAL_STATUSES[statusKey] : null;
                  return (
                    <div key={key} style={{ background: bg, border: `0.5px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, background: tag, color: '#fff', letterSpacing: '0.06em' }}>{label.toUpperCase()}</span>
                        {st && <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{st.label.toUpperCase()}</span>}
                      </div>
                      <p style={{ fontSize: 13, color: B.textPrimary, lineHeight: 1.6, margin: 0 }}>{review?.[`${key}_update`] || '—'}</p>
                    </div>
                  );
                })}
              </div>

              <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 12, textTransform: 'uppercase' }}>Next 6-month priorities</p>
              <div style={{ display: 'flex', gap: 10, marginBottom: 28 }}>
                {PRIORITY_META.map(({ key, label, tag, bg, border }) => (
                  <div key={key} style={{ flex: 1, background: bg, border: `0.5px solid ${border}`, borderRadius: 8, padding: '10px 12px' }}>
                    <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99, background: tag, color: '#fff', display: 'inline-block', marginBottom: 8 }}>{label.toUpperCase()}</span>
                    <p style={{ fontSize: 12, color: B.textPrimary, lineHeight: 1.6, margin: 0 }}>{review?.[`next_${key}`] || '—'}</p>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: `0.5px solid ${B.border}`, marginBottom: 24 }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, textTransform: 'uppercase', margin: 0 }}>Overall mid-year comments</p>
                {savedAt && !isCompleted && <span style={{ fontSize: 11, color: B.textMuted }}>Draft saved at {savedAt}</span>}
              </div>
              <p style={{ fontSize: 12, color: B.textMuted, marginBottom: 10, lineHeight: 1.5 }}>A short overall summary. This will be shared with the employee.</p>
              {isCompleted
                ? <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
                    <p style={{ fontSize: 13, color: B.textPrimary, lineHeight: 1.7 }}>{review.manager_comments}</p>
                  </div>
                : <textarea value={comments} onChange={e => setComments(e.target.value)} rows={5} placeholder="Share your overall assessment of this employee's mid-year performance..."
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, resize: 'vertical', padding: '10px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, fontFamily: 'inherit', lineHeight: 1.6, marginBottom: 16 }} />
              }
              {!isCompleted && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={saveDraft} disabled={!comments.trim() || saving}
                      style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 20px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: comments.trim() ? B.textSecondary : B.textMuted, cursor: comments.trim() ? 'pointer' : 'not-allowed' }}>
                      {saving ? 'SAVING...' : 'SAVE DRAFT'}
                    </button>
                    {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
                  </div>
                  <button onClick={submitReview} disabled={!canSubmit || saving}
                    style={{ fontSize: 12, letterSpacing: '0.08em', padding: '10px 28px', borderRadius: 6, border: 'none', background: canSubmit ? B.purple : B.border, color: canSubmit ? '#fff' : B.textMuted, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
                    {saving ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginBottom: 24 }}>
          {[{ label: 'Total reviews', val: reviews.length }, { label: 'Pending', val: pending, color: B.gold }, { label: 'Completed', val: completed, color: B.purple }].map(({ label, val, color }) => (
            <div key={label} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem' }}>
              <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>{label}</p>
              <p style={{ fontSize: 22, color: color || B.textPrimary, margin: 0 }}>{val}</p>
            </div>
          ))}
        </div>
        <div style={{ background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 10, marginBottom: 20 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: B.purple, marginTop: 5, flexShrink: 0 }} />
          <p style={{ fontSize: 12, color: B.textSecondary, lineHeight: 1.6, margin: 0 }}>Mid-year reviews are a lighter check-in. Review each employee's priority progress and share one set of overall comments. No rating required. Use <span style={{ color: B.textPrimary }}>Save Draft</span> to come back later without losing your work.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {reviews.map(item => {
            const { employee: emp, review } = item;
            const isComp = review?.manager_status === 'completed';
            const statusColor = isComp ? B.purple : review?.emp_status === 'submitted' ? B.gold : B.textMuted;
            const statusBg = isComp ? B.purpleDeep : review?.emp_status === 'submitted' ? '#0f0d05' : B.surfaceLight;
            const statusBorder = isComp ? B.purpleDim : review?.emp_status === 'submitted' ? B.goldDim : B.border;
            const statusLabel = isComp ? 'COMPLETED' : review?.emp_status === 'submitted' ? 'PENDING' : 'NOT SUBMITTED';
            return (
              <button key={emp.id} onClick={() => openReview(item)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'border-color .15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = B.purpleDim}
                onMouseLeave={e => e.currentTarget.style.borderColor = B.border}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purpleDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: B.purple, flexShrink: 0 }}>{initials(emp.name)}</div>
                  <div>
                    <p style={{ fontSize: 13, color: B.textPrimary, margin: 0 }}>{emp.name}</p>
                    <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0' }}>{emp.department}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10, letterSpacing: '0.05em', padding: '2px 10px', borderRadius: 99, background: statusBg, color: statusColor, border: `0.5px solid ${statusBorder}` }}>{statusLabel}</span>
                  <span style={{ color: B.textMuted, fontSize: 13 }}>→</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
